import { useState } from 'react';
import { Heart, MapPin, Package, Tag, Eye, Clock, Edit, Trash2, MoreVertical } from 'lucide-react';
import { getFileUrl } from '../../services/filesService';
import { PRODUCT_STATUS, LISTING_TYPE, PRODUCT_CONDITION } from '../../services/productsService';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Product Card Component
 * Displays a product in a card format
 */
const ProductCard = ({
    product,
    language = 'fr',
    onEdit,
    onDelete,
    onView,
    onToggleFavorite,
    isOwner = false,
    isFavorite = false,
    showActions = true
}) => {
    const { t } = useLanguage();
    const tp = t.products || {};
    const [showMenu, setShowMenu] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Get main photo URL
    const getMainPhotoUrl = () => {
        if (product.photos && product.photos.length > 0) {
            const mainPhoto = product.photos.find(p => p.isMain || p.ismain || p.ISMAIN) || product.photos[0];
            const filePath = mainPhoto.filePath || mainPhoto.filepath || mainPhoto.FILEPATH;
            return filePath ? getFileUrl(filePath) : null;
        }
        return null;
    };

    const photoUrl = getMainPhotoUrl();

    // Format price
    const formatPrice = () => {
        if (product.listingType === LISTING_TYPE.GIVE || !product.price) {
            return tp.free;
        }
        return new Intl.NumberFormat(language === 'fr' ? 'fr-BE' : 'en-US', {
            style: 'currency',
            currency: product.currency || 'EUR'
        }).format(product.price);
    };

    // Get status badge
    const getStatusBadge = () => {
        if (product.status === PRODUCT_STATUS.SOLD) {
            return { text: tp.sold, color: 'bg-red-500' };
        }
        if (product.status === PRODUCT_STATUS.RESERVED) {
            return { text: tp.reserved, color: 'bg-yellow-500' };
        }
        if (product.status === PRODUCT_STATUS.INACTIVE) {
            return { text: tp.inactive, color: 'bg-gray-500' };
        }
        return null;
    };

    const statusBadge = getStatusBadge();

    return (
        <div
            className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
                product.status !== PRODUCT_STATUS.ACTIVE ? 'opacity-75' : ''
            }`}
            onClick={() => onView?.(product)}
        >
            {/* Image */}
            <div className="relative aspect-square bg-gray-100">
                {photoUrl && !imageError ? (
                    <img
                        src={photoUrl}
                        alt={product.title}
                        className="w-full h-full object-cover"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-300" />
                    </div>
                )}

                {/* Status badge */}
                {statusBadge && (
                    <div className={`absolute top-2 left-2 ${statusBadge.color} text-white text-xs font-semibold px-2 py-1 rounded-full`}>
                        {statusBadge.text}
                    </div>
                )}

                {/* Listing type badge */}
                <div className={`absolute top-2 right-2 ${
                    product.listingType === LISTING_TYPE.GIVE ? 'bg-green-500' : 'bg-cabaza-purple'
                } text-white text-xs font-semibold px-2 py-1 rounded-full`}>
                    {product.listingType === LISTING_TYPE.GIVE ? tp.toGive : tp.toSell}
                </div>

                {/* Condition badge */}
                <div className={`absolute bottom-2 left-2 ${
                    product.condition === PRODUCT_CONDITION.NEW ? 'bg-blue-500' : 'bg-gray-600'
                } text-white text-xs px-2 py-1 rounded-full`}>
                    {product.condition === PRODUCT_CONDITION.NEW ? tp.new : tp.used}
                </div>

                {/* Favorite button */}
                {!isOwner && showActions && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleFavorite?.(product);
                        }}
                        className="absolute bottom-2 right-2 p-2 bg-white/90 rounded-full shadow-sm hover:bg-white transition-colors"
                    >
                        <Heart
                            className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                        />
                    </button>
                )}

                {/* Owner menu */}
                {isOwner && showActions && (
                    <div className="absolute bottom-2 right-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowMenu(!showMenu);
                            }}
                            className="p-2 bg-white/90 rounded-full shadow-sm hover:bg-white transition-colors"
                        >
                            <MoreVertical className="w-5 h-5 text-gray-600" />
                        </button>

                        {showMenu && (
                            <div className="absolute bottom-12 right-0 bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[120px] z-10">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMenu(false);
                                        onEdit?.(product);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Edit className="w-4 h-4" />
                                    {tp.edit}
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMenu(false);
                                        onDelete?.(product);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {tp.delete}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-4">
                {/* Category */}
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                    <Tag className="w-3 h-3" />
                    <span>{product.typeName || product.typename || product.TYPENAME}</span>
                </div>

                {/* Title */}
                <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2">
                    {product.title}
                </h3>

                {/* Price */}
                <div className="flex items-center justify-between">
                    <div>
                        <span className={`text-lg font-bold ${
                            product.listingType === LISTING_TYPE.GIVE ? 'text-green-600' : 'text-cabaza-purple'
                        }`}>
                            {formatPrice()}
                        </span>
                        {product.priceNegotiable && product.listingType !== LISTING_TYPE.GIVE && (
                            <span className="ml-2 text-xs text-gray-500">({tp.negotiable})</span>
                        )}
                    </div>
                </div>

                {/* Location & Stats */}
                <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                    {product.pickupAddress && (
                        <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate max-w-[120px]">{product.pickupAddress}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        <span>{product.viewCount || 0} {tp.views}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
